import { useState } from 'react';
import { Category } from '@/types/Category';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import { useBoolean } from 'minimal-shared/hooks';
import { Iconify } from 'src/components/iconify';
import { Paper, useTheme } from '@mui/material';
import Icon, { getIconName } from '@/components/Icon/Icon';

type PropTypes = {
  categories: Category[];
  onCategorySelect: (category: Category) => void;
  defaultSelected: string | undefined;
};

export function FaqsCategory({ categories, defaultSelected, onCategorySelect }: PropTypes) {
  const navOpen = useBoolean();
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(defaultSelected);

  const handleSelectCategory = (category: Category) => {
    setSelectedCategory(category.icon);
    onCategorySelect?.(category);
    navOpen.onFalse(); // Close drawer when selecting on mobile
  };

  const renderMobile = () => (
    <>
      <Box
        sx={[
          (theme) => ({
            p: 2,
            top: 0,
            left: 0,
            width: 1,
            display: { xs: 'block', md: 'none' },
          }),
        ]}>
        <Button color='primary' startIcon={<Iconify icon="solar:list-bold" />} onClick={navOpen.onTrue}>
          Categories
        </Button>
      </Box>

      <Drawer open={navOpen.value} onClose={navOpen.onFalse}>
        <Box
          sx={{
            p: 1,
            gap: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
          }}>
          {categories.map((category) => (
            <ItemMobile 
              key={category.icon} 
              category={category} 
              isSelected={selectedCategory === category.icon}
              onSelect={handleSelectCategory}
            />
          ))}
        </Box>
      </Drawer>
    </>
  );

  const renderDesktop = () => (
    <Box
      sx={{
        pt: 8,
        pb: 8,
        gap: 3,
        display: { xs: 'none', md: 'grid' },
        gridTemplateColumns: { md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
      }}>
      {categories.map((category) => (
        <ItemDesktop 
          key={category.icon} 
          category={category} 
          isSelected={selectedCategory === category.icon}
          onSelect={handleSelectCategory}
        />
      ))}
    </Box>
  );

  return (
    <>
      {renderMobile()}
      {renderDesktop()}
    </>
  );
}

// ----------------------------------------------------------------------

type ItemProps = {
  isSelected: boolean;
  category: Category;
  onSelect?: (category: Category) => void;
};

function ItemDesktop({ category, isSelected, onSelect }: ItemProps) {
  const theme = useTheme();

  return (
    <Paper
      variant="outlined"
      onClick={() => onSelect && onSelect(category)}
      sx={[
        (theme) => ({
          p: 2,
          borderRadius: 0,
          cursor: 'pointer',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
          transition: 'all 0.2s ease-in-out',
          '&:hover': { boxShadow: theme.customShadows.z16 },
        }),
      ]}>
      <Typography
        variant="h6"
        sx={(theme) => ({
          color: isSelected 
            ? theme.palette.primary.dark
            : theme.palette.primary.main,
        })}>
        {category.title}
      </Typography>
      {category.icon && 
        <Box 
          sx={{
            pt: 2,
            pb: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'end',
            flexGrow: 1,
          }}
        >
          <Icon name={getIconName(category.icon, 'unknown')} size="48" 
            color={isSelected 
              ? theme.palette.primary.dark
              : theme.palette.primary.main}  />
        </Box>
      }
    </Paper>
  );
}

// ----------------------------------------------------------------------

function ItemMobile({ category, isSelected, onSelect }: ItemProps) {
  const theme = useTheme();
  return (
    <ListItemButton
      onClick={() => onSelect && onSelect(category)}
      sx={{
        py: 2,
        maxWidth: 140,
        textAlign: 'center',
        alignItems: 'center',
        typography: 'caption',
        flexDirection: 'column',
        justifyContent: 'center',
        borderColor: isSelected ? theme.palette.primary.main : 'transparent',
        borderWidth: isSelected ? 1 : 0,
        borderStyle: 'solid',
        '&:hover': { bgcolor: theme.palette.background.paper },
      }}>
      
      <Typography
        variant="caption"
        sx={(theme) => ({
          color: isSelected 
            ? theme.palette.primary.dark
            : theme.palette.primary.main,
        })}>
        {category.title}
      </Typography>
      
      {category.icon && 
        <Box 
          sx={{
            pt: 2,
            pb: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'end',
            flexGrow: 1,
          }}
        >
          <Icon name={getIconName(category.icon, 'unknown')} size="56" 
            color={isSelected 
              ? theme.palette.primary.dark
              : theme.palette.primary.main}  />
        </Box>
      }
    </ListItemButton>
  );
}
